/**
 * Shared property listing form used by Landlord, Professional, and Student dashboards.
 * Props:
 *   initialValues  – pre-filled values for editing (defaults to EMPTY_FORM)
 *   onSubmit(form) – called when "List Property" / "Save Changes" is clicked
 *   onSaveDraft(form) – called when "Save as Draft" is clicked
 *   onCancel()     – called when "Cancel" or "Back" is clicked
 *   editingId      – string if editing an existing property, null if new
 *   submitting     – bool
 *   submitSuccess  – bool
 *   submitError    – string
 */
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import ImageUpload from '../ImageUpload';
import { CheckCircle } from 'lucide-react';

const AU_CITIES_BY_STATE = {
  'NSW': ['Sydney','Newcastle','Wollongong','Central Coast','Maitland','Bathurst','Albury','Wagga Wagga','Port Macquarie','Dubbo','Orange','Tamworth','Armidale','Lismore','Coffs Harbour','Broken Hill'],
  'VIC': ['Melbourne','Geelong','Ballarat','Bendigo','Shepparton','Wodonga','Warrnambool','Mildura','Latrobe Valley','Horsham'],
  'QLD': ['Brisbane','Gold Coast','Sunshine Coast','Townsville','Cairns','Toowoomba','Mackay','Rockhampton','Ipswich','Bundaberg','Hervey Bay','Gladstone','Mount Isa'],
  'WA':  ['Perth','Mandurah','Bunbury','Geraldton','Kalgoorlie','Albany','Broome','Port Hedland','Karratha'],
  'SA':  ['Adelaide','Mount Gambier','Whyalla','Murray Bridge','Victor Harbor','Port Lincoln','Port Augusta'],
  'ACT': ['Canberra'],
  'TAS': ['Hobart','Launceston','Devonport','Burnie'],
  'NT':  ['Darwin','Palmerston','Alice Springs','Katherine'],
};
const AU_STATES = ['NSW','VIC','QLD','WA','SA','ACT','TAS','NT'];

export const EMPTY_LISTING = {
  name: '', city: '', description: '',
  street_address: '', apt_no: '', suburb: '', postcode: '', state: '',
  price_per_week: '', room_type: 'Private Room',
  bedrooms: 1, bathrooms: 1,
  university_nearby: '', available_from: '', available_until: '', amenities: [],
  images: [],
  furnishing: 'Furnished', lease_term: 'Flexible',
  parking: 'Not Available', pets: 'Not Allowed',
  bills_included: 'Some Bills Included',
  room_features: [], kitchen_features: [],
  customAmenities: [], newAmenity: '',
  visibility: 'public',
};

const PillBtn = ({ label, active, onClick }) => (
  <button type="button" onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${active ? 'text-white border-transparent' : 'border-sky-200 text-gray-600 hover:border-[#094f6f]'}`}
    style={active ? { background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' } : {}}>
    {label}
  </button>
);

const SectionLabel = ({ children }) => (
  <p className="text-sm font-semibold text-gray-700 mb-2 mt-1">{children}</p>
);

export default function PropertyListingForm({
  initialValues = {},
  onSubmit,
  onSaveDraft,
  onSubmitForReview,
  onCancel,
  editingId = null,
  editingStatus = null,
  submitting = false,
  submitSuccess = false,
  submitError = '',
  draftSaved = false,
  title,
}) {
  const [form, setForm] = useState({ ...EMPTY_LISTING, ...initialValues });
  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) onSubmit(form);
  };

  const pill = (key, value) => {
    const current = form[key] || [];
    set(key, current.includes(value) ? current.filter(x => x !== value) : [...current, value]);
  };

  const amenitiesList = [
    'WiFi','Internet','1 Bathroom','2 Bathrooms','1 Toilet','2 Toilets',
    'Common Room','AC','Heating','Washing Machine','Dryer',
    'Gym','Pool','Parking (Free)','Parking (Street)','Parking (Paid)',
    'Balcony','Courtyard','Garden','Security','Fireplace',
    'Study Room','Bike Storage','Couples OK','Pets Allowed',
    ...(form.customAmenities || []),
  ];

  const roomFeatures = [
    '1 Single Bed','2 Single Beds','1 Double Bed','1 Queen Bed','1 King Bed',
    'Mattress Included','Wardrobe','Study Desk','Study Chair','Desk Lamp',
    'Bedside Table','Built-in Shelving',
  ];

  const kitchenFeatures = [
    'Microwave','Toaster','Hot Water Jug / Kettle','Refrigerator','2 Refrigerators',
    'Oven & Stovetop','Dishwasher','Coffee Machine','Blender','Rice Cooker',
    'Shared Kitchen','Private Kitchen',
  ];

  return (
    <div className="space-y-4 max-w-4xl">
      {title && (
        <div className="flex items-center gap-4 mb-2">
          <Button type="button" onClick={onCancel} variant="outline" size="sm">← Back</Button>
          <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
        </div>
      )}

      {submitSuccess && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          <CheckCircle className="w-5 h-5" />
          {editingId ? 'Changes saved!' : 'Listing published! It\'s now visible to students.'}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-[#094f6f]">Property Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">

            {/* Name + City */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <SectionLabel>Property / Room Title *</SectionLabel>
                <Input value={form.name} onChange={e => set('name', e.target.value)}
                  placeholder="e.g., Sunny Studio near UniMelb" required
                  className="border-sky-200 focus:border-[#094f6f]" />
              </div>
              <div>
                <SectionLabel>City *</SectionLabel>
                <Select value={form.city} onValueChange={v => set('city', v)}>
                  <SelectTrigger className="border-sky-200"><SelectValue placeholder="Select city" /></SelectTrigger>
                  <SelectContent className="max-h-64 overflow-y-auto">
                    {Object.entries(AU_CITIES_BY_STATE).map(([state, cities]) => (
                      <React.Fragment key={state}>
                        <div className="px-2 py-1 text-xs font-bold text-gray-400 uppercase tracking-wide bg-gray-50">{state}</div>
                        {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Structured Address */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <SectionLabel>Street Address</SectionLabel>
                <Input value={form.street_address} onChange={e => set('street_address', e.target.value)}
                  placeholder="e.g., 123 Collins Street" className="border-sky-200 focus:border-[#094f6f]" />
              </div>
              <div>
                <SectionLabel>Apartment / Unit / House No. <span className="text-gray-400 font-normal">(optional)</span></SectionLabel>
                <Input value={form.apt_no} onChange={e => set('apt_no', e.target.value)}
                  placeholder="e.g., Unit 4" className="border-sky-200 focus:border-[#094f6f]" />
              </div>
              <div>
                <SectionLabel>Suburb</SectionLabel>
                <Input value={form.suburb} onChange={e => set('suburb', e.target.value)}
                  placeholder="e.g., Carlton" className="border-sky-200 focus:border-[#094f6f]" />
              </div>
              <div>
                <SectionLabel>Postcode</SectionLabel>
                <Input value={form.postcode} onChange={e => set('postcode', e.target.value.replace(/\D/g,'').slice(0,4))}
                  placeholder="e.g., 3053" maxLength={4} className="border-sky-200 focus:border-[#094f6f]" />
              </div>
              <div>
                <SectionLabel>State</SectionLabel>
                <Select value={form.state} onValueChange={v => set('state', v)}>
                  <SelectTrigger className="border-sky-200"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>{AU_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* Price + Room type + Beds + Baths */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <SectionLabel>Price/Week (AUD) *</SectionLabel>
                <Input type="number" value={form.price_per_week} onChange={e => set('price_per_week', e.target.value)}
                  placeholder="350" required min="1" className="border-sky-200 focus:border-[#094f6f]" />
              </div>
              <div>
                <SectionLabel>Room Type</SectionLabel>
                <Select value={form.room_type} onValueChange={v => set('room_type', v)}>
                  <SelectTrigger className="border-sky-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Private Room','Shared Room','Studio','1-Bedroom','2-Bedroom','Entire House','Entire Apartment'].map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <SectionLabel>Bedrooms</SectionLabel>
                <Select value={String(form.bedrooms)} onValueChange={v => set('bedrooms', parseInt(v))}>
                  <SelectTrigger className="border-sky-200"><SelectValue /></SelectTrigger>
                  <SelectContent>{[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <SectionLabel>Bathrooms</SectionLabel>
                <Select value={String(form.bathrooms)} onValueChange={v => set('bathrooms', parseInt(v))}>
                  <SelectTrigger className="border-sky-200"><SelectValue /></SelectTrigger>
                  <SelectContent>{[1,2,3,4].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* University + Available From + Available Until */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <SectionLabel>Nearby University</SectionLabel>
                <Input value={form.university_nearby} onChange={e => set('university_nearby', e.target.value)}
                  placeholder="e.g., University of Melbourne" className="border-sky-200 focus:border-[#094f6f]" />
              </div>
              <div>
                <SectionLabel>Available From</SectionLabel>
                <Input type="date" value={form.available_from} onChange={e => set('available_from', e.target.value)}
                  className="border-sky-200 focus:border-[#094f6f]" />
              </div>
              <div>
                <SectionLabel>Available Until <span className="text-gray-400 font-normal">(leave blank if ongoing)</span></SectionLabel>
                <Input type="date" value={form.available_until || ''} onChange={e => set('available_until', e.target.value)}
                  min={form.available_from || undefined}
                  className="border-sky-200 focus:border-[#094f6f]" />
              </div>
            </div>

            {/* Description */}
            <div>
              <SectionLabel>Description *</SectionLabel>
              <textarea value={form.description} onChange={e => set('description', e.target.value)}
                placeholder="Describe your property, nearby facilities, transport links, house rules..."
                rows={4} required
                className="w-full px-3 py-2 border border-sky-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#094f6f] text-sm resize-y" />
            </div>

            {/* Policy dropdowns */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { key: 'furnishing', label: 'Furnishing', opts: ['Furnished', 'Unfurnished', 'Partially Furnished'] },
                { key: 'lease_term', label: 'Minimum Lease', opts: ['Flexible', '1 Month', '3 Months', '6 Months', '12 Months'] },
                { key: 'parking', label: 'Parking', opts: ['Not Available', 'Available (Free)', 'Available (Extra Cost)'] },
                { key: 'pets', label: 'Pet Policy', opts: ['Not Allowed', 'Allowed', 'Negotiable'] },
                { key: 'bills_included', label: 'Bills', opts: ['Not Included', 'All Bills Included', 'Some Bills Included', 'Water & Internet Only'] },
              ].map(({ key, label, opts }) => (
                <div key={key}>
                  <SectionLabel>{label}</SectionLabel>
                  <Select value={form[key]} onValueChange={v => set(key, v)}>
                    <SelectTrigger className="border-sky-200"><SelectValue /></SelectTrigger>
                    <SelectContent>{opts.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            {/* Amenities */}
            <div>
              <SectionLabel>Amenities</SectionLabel>
              <div className="flex flex-wrap gap-2 mb-3">
                {amenitiesList.map(a => (
                  <PillBtn key={a} label={a} active={form.amenities.includes(a)}
                    onClick={() => set('amenities', form.amenities.includes(a) ? form.amenities.filter(x => x !== a) : [...form.amenities, a])} />
                ))}
              </div>
              <div className="flex gap-2 items-center">
                <Input value={form.newAmenity || ''} onChange={e => set('newAmenity', e.target.value)}
                  placeholder="Type a new amenity..."
                  className="border-sky-200 h-8 text-xs flex-1"
                  onKeyDown={e => {
                    if (e.key === 'Enter' && form.newAmenity?.trim()) {
                      e.preventDefault();
                      const a = form.newAmenity.trim();
                      set('customAmenities', [...(form.customAmenities || []), a]);
                      set('amenities', [...form.amenities, a]);
                      set('newAmenity', '');
                    }
                  }}
                />
                <Button type="button" size="sm" className="h-8 text-xs text-white px-3"
                  style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}
                  onClick={() => {
                    const a = (form.newAmenity || '').trim();
                    if (!a) return;
                    set('customAmenities', [...(form.customAmenities || []), a]);
                    set('amenities', [...form.amenities, a]);
                    set('newAmenity', '');
                  }}>
                  + Create Amenity
                </Button>
              </div>
            </div>

            {/* Room Features */}
            <div>
              <SectionLabel>Room Features</SectionLabel>
              <div className="flex flex-wrap gap-2">
                {roomFeatures.map(f => <PillBtn key={f} label={f} active={(form.room_features||[]).includes(f)} onClick={() => pill('room_features', f)} />)}
              </div>
            </div>

            {/* Kitchen Features */}
            <div>
              <SectionLabel>Kitchen Features</SectionLabel>
              <div className="flex flex-wrap gap-2">
                {kitchenFeatures.map(f => <PillBtn key={f} label={f} active={(form.kitchen_features||[]).includes(f)} onClick={() => pill('kitchen_features', f)} />)}
              </div>
            </div>

            {/* Photos */}
            <div>
              <SectionLabel>Property Photos <span className="text-gray-400 font-normal">(up to 5, first is the cover)</span></SectionLabel>
              <ImageUpload
                images={form.images}
                onChange={urls => set('images', urls)}
                folder="rentalx/listings"
              />
            </div>

            {/* Visibility toggle — positioned directly above the form action buttons */}
            <div className="border-2 border-[#094f6f]/20 bg-[#094f6f]/3 rounded-2xl p-4">
              <p className="text-sm font-bold text-gray-800 mb-3">Listing Visibility</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { val: 'public',  icon: '🌐', label: 'Public',  sub: 'Visible in all search results' },
                  { val: 'private', icon: '🔒', label: 'Private', sub: 'Shareable link only, not in search' },
                ].map(opt => (
                  <button key={opt.val} type="button" onClick={() => set('visibility', opt.val)}
                    data-testid={`visibility-${opt.val}-btn`}
                    className={`flex items-start gap-2.5 p-3 rounded-xl border-2 text-left transition-all ${
                      form.visibility === opt.val ? 'border-[#094f6f] bg-white shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}>
                    <span className="text-lg mt-0.5">{opt.icon}</span>
                    <div>
                      <p className={`text-sm font-bold ${form.visibility === opt.val ? 'text-[#094f6f]' : 'text-gray-700'}`}>{opt.label}</p>
                      <p className="text-xs text-gray-400">{opt.sub}</p>
                    </div>
                  </button>
                ))}
              </div>
              {form.visibility === 'private' && (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mt-3">
                  🔒 A unique private link will be generated after submission. Only people with the link can view this listing.
                </p>
              )}
            </div>

            {submitError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-600">{submitError}</div>
            )}

            {/* Action buttons */}
            <div className="flex justify-between pt-2 flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
              <div className="flex gap-2">
                {/* CASE A — editing a DRAFT: Save as Draft (secondary) + Submit for Review (primary) */}
                {editingId && editingStatus === 'draft' ? (
                  <>
                    <Button type="button" variant="outline"
                      disabled={draftSaved}
                      className={draftSaved ? "border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed px-6" : "border-gray-300 text-gray-600 hover:bg-gray-50 px-6"}
                      onClick={() => onSaveDraft && onSaveDraft(form)}>
                      {draftSaved ? '✓ Draft Saved' : 'Save as Draft'}
                    </Button>
                    <Button type="button"
                      className="text-white hover:opacity-90 px-8 font-bold"
                      style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}
                      disabled={submitting}
                      onClick={() => onSubmitForReview && onSubmitForReview(form)}>
                      {submitting ? 'Submitting…' : 'Submit for Review'}
                    </Button>
                  </>
                ) : editingId ? (
                  /* CASE B — editing a non-draft (pending_review, active, archived): Save Changes only */
                  <Button type="submit" disabled={submitting}
                    className="text-white hover:opacity-90 px-8"
                    style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
                    {submitting ? 'Saving...' : 'Save Changes'}
                  </Button>
                ) : (
                  /* CASE C — new listing: Save as Draft (secondary) + Submit for Review (primary) */
                  <>
                    <Button type="button" variant="outline"
                      disabled={!form.city || draftSaved}
                      className={draftSaved ? "border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed px-6" : "border-gray-300 text-gray-600 hover:bg-gray-50 px-6"}
                      onClick={() => onSaveDraft && onSaveDraft(form)}>
                      {draftSaved ? '✓ Draft Saved' : 'Save as Draft'}
                    </Button>
                    <Button type="submit" disabled={submitting || !form.city}
                      className="text-white hover:opacity-90 px-8 font-bold"
                      style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
                      {submitting ? 'Submitting...' : 'Submit for Review'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
